// App.js
import React, { useEffect, useState, useContext } from "react";
import { Navigate } from "react-router-dom";
import { secureStorage } from "./utils/secureStorage";

import Chatbot from "./Chatbot";
import Dashboard from "./Dashboard";
import Sidebar from "./LLMSidebar";
import SQLQuery from "./SQLQuery";
import Tables from "./Tables";
import TextSummarization from "./TextSummarization";
import SuperAdminDashboard from "./SuperAdminDashboard";
import Billing from "./Billing";

import { ThemeContext, ThemeProvider } from "./ThemeContext";
import "./App.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ProtectedApp() {
  const [appReady, setAppReady] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [feature, setFeature] = useState("Chatbot");
  const [activeChat, setActiveChat] = useState(null);
  const [resumeAvailable, setResumeAvailable] = useState(false);
  const [latestSessionId, setLatestSessionId] = useState(null);
  const [chatResetSignal, setChatResetSignal] = useState(0);
  const [role, setRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasPaid, setHasPaid] = useState(true);
  const [tier, setTier] = useState("Free");

  const { theme, toggleTheme } = useContext(ThemeContext);

  const handleChatReset = () => {
    setActiveChat(null);
    secureStorage.removeItem("activeChat");
    setChatResetSignal((prev) => prev + 1);
    setFeature("Chatbot");
    secureStorage.removeItem("feature", "Chatbot");
  };

  // ✅ Add handleLoginSuccess function
  const handleLoginSuccess = () => {
    // Re-check authentication immediately after login
    const token = secureStorage.getItem("token");
    const storedRole = secureStorage.getItem("role");
    const storedSuperAdmin = secureStorage.getItem("isSuperAdmin") === "true";
    const storedTier = secureStorage.getItem("tier") || "Free";
    const accessExpiresAt = secureStorage.getItem("accessExpiresAt");
    
    const paid = accessExpiresAt && new Date(accessExpiresAt) > new Date();
    
    setIsAuthenticated(!!token);
    setRole(storedRole);
    setIsSuperAdmin(storedSuperAdmin);
    setTier(storedTier);
    setHasPaid(paid || storedTier === "Free");
    setFeature("Chatbot"); // Reset to default feature
  };

  useEffect(() => {
    let isMounted = true;

    // Max timer fallback (e.g. 10s)
    const maxTimeout = setTimeout(() => {
      if (isMounted) setAppReady(true);
    }, 10000);

    const initializeApp = async () => {
      try {
        // 1. Load SessionStorage
        const token = secureStorage.getItem("token");
        const storedFeature = secureStorage.getItem("feature") || "Chatbot";
        const storedChat = secureStorage.getItem("activeChat");
        const storedRole = secureStorage.getItem("role");
        const storedSuperAdmin = secureStorage.getItem("isSuperAdmin") === "true";
        const storedTier = secureStorage.getItem("tier") || "Free";
        const accessExpiresAt = secureStorage.getItem("accessExpiresAt");

        const paid =
          accessExpiresAt && new Date(accessExpiresAt) > new Date();

        setIsAuthenticated(!!token);
        setFeature(storedFeature);
        setActiveChat(storedChat);
        setRole(storedRole);
        setIsSuperAdmin(storedSuperAdmin);
        setTier(storedTier);
        setHasPaid(paid || storedTier === "Free");

        // 2. Fetch session data if needed
        if (!!token && storedFeature === "Chatbot" && !storedSuperAdmin) {
          const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const sessions = await response.json();

          if (!storedChat && Array.isArray(sessions) && sessions.length >= 10) {
            const sorted = sessions.sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            const latest = sorted[0];
            setResumeAvailable(true);
            setLatestSessionId(latest.id);
          }
        }

        // 3. Set ready
        if (isMounted) {
          clearTimeout(maxTimeout);
          setAppReady(true);
        }
      } catch (error) {
        console.error("⚠️ App init failed:", error);
        if (isMounted) {
          clearTimeout(maxTimeout);
          setAppReady(true); // fallback
        }
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
      clearTimeout(maxTimeout);
    };
  }, []);

  const handleResumeChat = () => {
    if (latestSessionId) {
      setActiveChat(latestSessionId);
      secureStorage.setItem("activeChat", latestSessionId);
      setResumeAvailable(false);
    }
  };

  const handleLogout = () => {
    secureStorage.clear();
    setIsAuthenticated(false);
    setFeature("Chatbot");
    setActiveChat(null);
    setRole(null);
    setTier("Free");
  };

  const handleNewChatCreated = () => {
    setResumeAvailable(false);
  };

  if (!appReady) {
    return (
      <div className="app-loading">
        <img
          src="/logo_white.svg"
          alt="Retreeval Logo"
          className="logo-bounce"
          style={{ width: "120px", marginBottom: "1rem" }}
        />
        <h2 style={{ textAlign: "center", color: "#000", fontWeight: 400 }}>
          Loading<span className="dots">...</span>
        </h2>
      </div>
    );
  }

  return isAuthenticated ? (
    hasPaid ? (
      <div className={`app-container ${theme}`}>
        <Sidebar
          onLogout={handleLogout}
          feature={feature}
          setFeature={setFeature}
          activeChat={activeChat}
          setActiveChat={setActiveChat}
          resetChatUI={handleChatReset}
          theme={theme}
          toggleTheme={toggleTheme}
        />
        <div className="main-content">
          {isSuperAdmin ? (
            <SuperAdminDashboard />
          ) : (
            <>
              {feature === "Chatbot" && (
                <Chatbot
                  activeChat={activeChat}
                  setActiveChat={setActiveChat}
                  onNewChatCreated={handleNewChatCreated}
                  resumeAvailable={resumeAvailable}
                  onResumeChat={handleResumeChat}
                  chatResetSignal={chatResetSignal}
                />
              )}
              {feature === "Dashboard" && <Dashboard />}
              {feature === "SQLQuery" && <SQLQuery />}
              {feature === "Tables" && <Tables />}
              {feature === "TextSummarization" && <TextSummarization />}
            </>
          )}
        </div>
      </div>
    ) : (
      <Billing />
    )
  ) : (
    <Navigate to="/login" replace />
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ProtectedApp />
    </ThemeProvider>
  );
}
